import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import ExpertLayout from '../../components/ExpertLayout';
import PageHeader from '../../components/expert/PageHeader';
import { expertDashboardApi, getErrorMessage, IntegrationInfo } from '../../lib/http';
import GoogleCalendarCard from '../../components/expert/integrations/GoogleCalendarCard';
import CalendarPickerModal from '../../components/expert/integrations/CalendarPickerModal';

const Integrations: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [isSelectingCalendar, setIsSelectingCalendar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await expertDashboardApi.integrations.list();
      return response.data as { integrations: IntegrationInfo[] };
    },
  });

  const googleIntegration = data?.integrations?.find(
    (i: IntegrationInfo) => i.kind === 'google_calendar'
  );

  const handleConnect = () => {
    // Redirect to Google OAuth
    window.location.href = expertDashboardApi.integrations.getGoogleOAuthUrl();
  };

  const disconnectMutation = useMutation({
    mutationFn: () => expertDashboardApi.integrations.disconnectGoogle(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Google Calendar disconnected');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => expertDashboardApi.integrations.syncGoogle(),
    onSuccess: () => {
      toast.success('Sync started');
      // Refetch after a short delay to show updated last_synced_at
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
      }, 2000);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSelectCalendar = async (calendarId: string) => {
    setIsSelectingCalendar(true);
    try {
      await expertDashboardApi.integrations.selectCalendar(calendarId);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowCalendarPicker(false);
      toast.success('Calendar selected');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSelectingCalendar(false);
    }
  };

  return (
    <ExpertLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Integrations"
          subtitle="Connect external services to enhance your workflow"
          backTo="/app/dashboard"
        />

        {/* Needs Attention Banner */}
        {googleIntegration?.needs_attention && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-amber-800">Attention Required</h3>
                <p className="mt-1 text-sm text-amber-700">
                  {googleIntegration.error_message || 'Your Google Calendar connection needs attention.'}
                </p>
                <button
                  onClick={handleConnect}
                  className="mt-2 text-sm font-medium text-amber-600 hover:text-amber-700"
                >
                  Reconnect &rarr;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Integration Cards */}
        <div className="space-y-4">
          <GoogleCalendarCard
            integration={googleIntegration}
            isLoading={isLoading}
            onConnect={handleConnect}
            onDisconnect={() => disconnectMutation.mutate()}
            onSync={() => syncMutation.mutate()}
            onSelectCalendar={() => setShowCalendarPicker(true)}
            isDisconnecting={disconnectMutation.isPending}
            isSyncing={syncMutation.isPending}
          />
        </div>

        {/* Future Integrations Placeholder */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Coming Soon</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.82 3H4.18A2.18 2.18 0 002 5.18v13.64A2.18 2.18 0 004.18 21h15.64A2.18 2.18 0 0022 18.82V5.18A2.18 2.18 0 0019.82 3zM12 18a6 6 0 116-6 6 6 0 01-6 6z"/>
                    <path d="M12 8v4l3 3"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Zoom</p>
                  <p className="text-xs text-gray-500">Video meetings</p>
                </div>
              </div>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 01-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Stripe</p>
                  <p className="text-xs text-gray-500">Payment processing</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Picker Modal */}
        <CalendarPickerModal
          isOpen={showCalendarPicker}
          onClose={() => setShowCalendarPicker(false)}
          onSelect={handleSelectCalendar}
          isSelecting={isSelectingCalendar}
        />
      </div>
    </ExpertLayout>
  );
};

export default Integrations;
